/**
 * Inscricao no beta do Cabal Next (dono, 2026-10-10): o site so' recebe cadastros por enquanto -- sem login.
 * Modal aberto pelo botao "Inscrever Beta" do header. Mesmo fluxo do cadastro por e-mail antigo
 * (features/Cadastro/CadastroEmail.tsx -> POST /Account/registerEmail):
 *   1. form (ID, senha, confirmar, nome, e-mail) sem Code -> a API confere ID/e-mail livres e manda o CODIGO
 *   2. mesmos dados + Code -> a API cria a conta (ja' verificada)
 *   3. pronto: avisa que o beta abre nos proximos dias.
 * Renderizado num portal no <body> (o header tem backdrop-filter, que prenderia o position:fixed). CSS em beta.css.
 */
import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, Instagram, Loader2, MailCheck, PartyPopper, X } from 'lucide-react';
import { registerEmail, type RegisterEmailRequest } from '@/shared/services/authServices';
import { OtpInput } from '@/features/Cadastro/CadastroParts';
import { LINKS } from './NextLayout';
import { LegalModal, type LegalTab } from './LegalModal';
import { Turnstile, isTurnstileEnabled, type TurnstileHandle } from './Turnstile';
import './beta.css';

type Step = 'form' | 'code' | 'done';

type BetaForm = {
  loginId: string;
  password: string;
  confirmPassword: string;
  name: string;
  email: string;
};

const EMPTY: BetaForm = { loginId: '', password: '', confirmPassword: '', name: '', email: '' };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// O erro do servidor chega como {detail: {Status, Message}}
const errMsg = (e: any, fallback: string) => {
  const b = e?.detail && typeof e.detail === 'object' ? e.detail : e;
  return String(b?.Message || b?.message || e?.message || fallback);
};

type Props = { open: boolean; onClose: () => void };

export function BetaSignup({ open, onClose }: Props) {
  const [step, setStep] = useState<Step>('form');
  const [form, setForm] = useState<BetaForm>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otp, setOtp] = useState('');
  const [dest, setDest] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [captcha, setCaptcha] = useState('');
  const captchaRef = useRef<TurnstileHandle>(null);
  const [accepted, setAccepted] = useState(false);          // aceite dos Termos + Privacidade (dono)
  const [legal, setLegal] = useState<LegalTab | null>(null); // modal dos termos aberto por cima
  const firstField = useRef<HTMLInputElement>(null);

  const setField = <K extends keyof BetaForm>(k: K, v: BetaForm[K]) => setForm((s) => ({ ...s, [k]: v }));

  // Fechar depois de concluir zera o modal; fechar no meio guarda o que foi digitado (reabrir continua dali)
  const close = () => {
    if (loading) return;
    if (step === 'done') {
      setStep('form');
      setForm(EMPTY);
      setOtp('');
      setAccepted(false);
    }
    setError(null);
    setLegal(null);
    onClose();
  };

  // Esc fecha (o modal dos termos primeiro); a pagina (o <body> e' quem rola) fica parada com o modal aberto
  useEffect(() => {
    if (!open) return;
    const body = document.body;
    const prev = body.style.overflow;
    body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (legal) setLegal(null);
      else close();
    };
    window.addEventListener('keydown', onKey);
    if (step === 'form') requestAnimationFrame(() => firstField.current?.focus());
    return () => {
      body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loading, step, legal]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  // Mesmas regras do cadastro antigo (CadastroEmail)
  const validate = (): string | null => {
    const loginId = form.loginId.trim();
    const name = form.name.trim();
    if (loginId.length < 4) return 'Informe um ID (mín. 4 caracteres).';
    if (loginId.length > 16) return 'Informe um ID (máx. 16 caracteres).';
    if (form.password.length < 6) return 'Senha muito curta (mín. 6 caracteres).';
    if (form.password !== form.confirmPassword) return 'As senhas não conferem.';
    if (name.length < 3) return 'Informe seu nome (mín. 3 caracteres).';
    if (name.length > 30) return 'Informe seu nome (máx. 30 caracteres).';
    if (!EMAIL_RE.test(form.email.trim())) return 'E-mail inválido.';
    if (!accepted) return 'Para se inscrever, aceite os Termos de Serviço e a Política de Privacidade.';
    if (isTurnstileEnabled() && !captcha) return 'Confirme que você não é um robô.';
    return null;
  };

  const body = (extra: Partial<RegisterEmailRequest>): RegisterEmailRequest => ({
    Username: form.loginId.trim(),
    Password: form.password,
    Email: form.email.trim().toLowerCase(),
    Name: form.name.trim(),
    ReferCode: null,
    ...extra,
  });

  // Passo 1: a API confere os dados e manda o codigo (a conta ainda nao existe)
  const sendCode = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await registerEmail(body({ Captcha: captcha || null }));
      const st = String(res?.Status ?? res?.status ?? '');
      if (st === 'CODE_SENT') {
        setDest(String(res?.Destination ?? res?.destination ?? form.email));
        setOtp('');
        setCooldown(Number(res?.RetryAfterSeconds ?? res?.retryAfterSeconds ?? 60));
        setStep('code');
      } else {
        setError(res?.Message || res?.message || 'Não foi possível enviar o código.');
      }
    } catch (e: any) {
      setError(errMsg(e, 'Não foi possível enviar o código. Tente novamente.'));
    } finally {
      // o token do captcha so' vale uma vez
      captchaRef.current?.reset();
      setCaptcha('');
      setLoading(false);
    }
  };

  const submitForm = (e: FormEvent) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    void sendCode();
  };

  // Passo 2: mesmos dados + codigo -> a conta e' criada
  const submitCode = async (e: FormEvent) => {
    e.preventDefault();
    if (otp.length !== 6) {
      setError('Digite o código completo (6 dígitos).');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await registerEmail(body({ Code: otp, Captcha: null }));
      const st = String(res?.Status ?? res?.status ?? '');
      if (st === 'Sucesso') setStep('done');
      else setError(res?.Message || res?.message || 'Não foi possível concluir a inscrição.');
    } catch (e: any) {
      setError(errMsg(e, 'Código inválido ou expirado.'));
    } finally {
      setLoading(false);
    }
  };

  // Reenviar = passo 1 de novo (com captcha ligado, pede o captcha outra vez)
  const resend = () => {
    if (cooldown > 0 || loading) return;
    if (isTurnstileEnabled() && !captcha) {
      setError('Confirme que você não é um robô para reenviar.');
      return;
    }
    void sendCode();
  };

  if (!open) return null;

  return createPortal(
    <div className="nxb-overlay" onMouseDown={(e) => e.target === e.currentTarget && close()}>
      <div className="nxb-modal" role="dialog" aria-modal="true" aria-labelledby="nxb-title">
        <button type="button" className="nxb-close" onClick={close} aria-label="Fechar" disabled={loading}>
          <X size={20} />
        </button>

        {step === 'form' && (
          <form onSubmit={submitForm} noValidate>
            <p className="nxb-eyebrow">Cabal Next · Beta</p>
            <h2 id="nxb-title" className="nxb-title">
              Inscrição no <span>Beta</span>
            </h2>
            <p className="nxb-lead">
              O beta será liberado nos próximos dias. Crie sua conta agora: é com este ID e senha que você vai entrar no
              jogo.
            </p>

            <label className="nxb-field">
              <span>ID</span>
              <input
                ref={firstField}
                value={form.loginId}
                onChange={(e) => setField('loginId', e.target.value)}
                placeholder="De 4 a 16 caracteres"
                autoComplete="username"
                maxLength={16}
              />
            </label>
            <div className="nxb-row">
              <label className="nxb-field">
                <span>Senha</span>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setField('password', e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  autoComplete="new-password"
                />
              </label>
              <label className="nxb-field">
                <span>Confirmar senha</span>
                <input
                  type="password"
                  value={form.confirmPassword}
                  onChange={(e) => setField('confirmPassword', e.target.value)}
                  placeholder="Repita a senha"
                  autoComplete="new-password"
                />
              </label>
            </div>
            <label className="nxb-field">
              <span>Nome</span>
              <input
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                placeholder="Seu primeiro nome"
                autoComplete="given-name"
                maxLength={30}
              />
            </label>
            <label className="nxb-field">
              <span>E-mail</span>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setField('email', e.target.value)}
                placeholder="voce@exemplo.com"
                autoComplete="email"
              />
              <small>Enviaremos um código de confirmação para este e-mail.</small>
            </label>

            {/* dono: aceite dos termos EM CIMA do captcha; os links abrem o modal com o texto */}
            <label className="nxb-check">
              <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
              <span>
                Li e aceito os{' '}
                <button type="button" onClick={() => setLegal('terms')}>
                  Termos de Serviço
                </button>{' '}
                e a{' '}
                <button type="button" onClick={() => setLegal('privacy')}>
                  Política de Privacidade
                </button>
                .
              </span>
            </label>

            {isTurnstileEnabled() && (
              <div className="nxb-captcha">
                <Turnstile ref={captchaRef} onToken={setCaptcha} />
              </div>
            )}

            {error && <p className="nxb-error">{error}</p>}

            <button type="submit" className="nxb-submit" disabled={loading}>
              {loading ? <Loader2 size={18} className="nxb-spin" /> : null}
              {loading ? 'Enviando código...' : 'Confirmar'}
            </button>
          </form>
        )}

        {step === 'code' && (
          <form onSubmit={submitCode} noValidate>
            <span className="nxb-badge">
              <MailCheck size={30} />
            </span>
            <h2 id="nxb-title" className="nxb-title nxb-title--center">
              Confirme seu <span>e-mail</span>
            </h2>
            <p className="nxb-lead nxb-lead--center">
              Enviamos um código de 6 dígitos para <b>{dest}</b>. Digite abaixo para concluir a inscrição.
              <small>Não chegou? Confira também a caixa de spam.</small>
            </p>

            <div className="nxb-otp">
              <OtpInput value={otp} onChange={setOtp} disabled={loading} length={6} />
            </div>

            {isTurnstileEnabled() && cooldown <= 0 && (
              <div className="nxb-captcha">
                <Turnstile ref={captchaRef} onToken={setCaptcha} />
              </div>
            )}

            <div className="nxb-links">
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  setStep('form');
                }}
                disabled={loading}
              >
                <ArrowLeft size={14} /> Voltar e editar
              </button>
              <button type="button" onClick={resend} disabled={loading || cooldown > 0}>
                {cooldown > 0 ? `Reenviar em ${cooldown}s` : 'Reenviar código'}
              </button>
            </div>

            {error && <p className="nxb-error">{error}</p>}

            <button type="submit" className="nxb-submit" disabled={loading || otp.length !== 6}>
              {loading ? <Loader2 size={18} className="nxb-spin" /> : null}
              {loading ? 'Validando...' : 'Concluir inscrição'}
            </button>
          </form>
        )}

        {step === 'done' && (
          <div className="nxb-done">
            <span className="nxb-badge nxb-badge--ok">
              <PartyPopper size={30} />
            </span>
            <h2 id="nxb-title" className="nxb-title nxb-title--center">
              Inscrição <span>confirmada!</span>
            </h2>
            <p className="nxb-lead nxb-lead--center">
              Sua conta <b>{form.loginId.trim()}</b> está criada. O beta será liberado nos próximos dias: siga o nosso
              Instagram para saber a data e baixar o jogo assim que abrir.
            </p>
            <a className="nxb-submit nxb-submit--insta" href={LINKS.instagram} target="_blank" rel="noopener noreferrer">
              <Instagram size={18} /> Seguir @cabalnext
            </a>
            <button type="button" className="nxb-ghost" onClick={close}>
              Fechar
            </button>
          </div>
        )}
      </div>
      {legal && (
        <LegalModal
          tab={legal}
          onTab={setLegal}
          onClose={() => setLegal(null)}
          onAccept={() => {
            setAccepted(true);
            setLegal(null);
          }}
        />
      )}
    </div>,
    document.body
  );
}
